import { defineComponent as _defineComponent } from 'vue'
import { ref, computed, onMounted, onUnmounted, nextTick, defineAsyncComponent, watch, getCurrentInstance } from 'vue';
import { datadogRum } from '@datadog/browser-rum';
import { getAppVariable } from '@white-label-helper/get-app-variable';
import { sanitizeHTML } from '@white-label-helper/shared-helpers';
// Types
import type { SearchProducts } from '@white-label-types/parking-booking';

// Components
import { CavuButton } from 'ui-shared-components';
import { TravelInsuranceQuoteDetails } from 'ecommerce/feature-travel-insurance';
import PoliciesMoreInfoModal from 'ecommerce/feature-search-product-list/policies-more-info-modal';

// Store
import { useTravelInsuranceStore } from '@white-label-store/travel-insurance';

interface ExtrasMoreInfoModalProps {
  product: SearchProducts;
  tabs?: { id: string; translationKey: string; visible: boolean; active: boolean; content: string; showPolicy?: boolean }[];
}
export default /*#__PURE__*/_defineComponent({
  __name: 'extras-more-info',
  props: {
    product: { default: () => ({}) },
    tabs: { default: () => [] }
  },
  emits: ['close', 'action'],
  setup(__props: any, { expose, emit }) {

const props = __props as { product: SearchProducts, tabs: { id: string; translationKey: string; visible: boolean; active: boolean; content: string; showPolicy?: boolean }[] };

// Packages
const IconArrow = defineAsyncComponent(() => import('@white-label-icon/icon-arrow'));
const IconClose = defineAsyncComponent(() => import('@white-label-icon/icon-close'));
const IconSuccess = defineAsyncComponent(() => import('@white-label-icon/icon-success'));
const IconPlus = defineAsyncComponent(() => import('@white-label-icon/icon-plus'));

;





const { proxy } = getCurrentInstance();

const travelInsuranceStore = useTravelInsuranceStore();

const moreInfoModal = ref<HTMLElement | null>(null);
const isTabsScrollable = ref(false);
const isTabsScrollAtTheEnd = ref(false);
const showProductLogo = computed(() => getAppVariable('is_product_logo_visible') && Boolean(props.product.logo?.src));
const localTabs = ref([]);
const isScrolling = ref(false);
const scrollTimeout = ref(null);

const isInBasket = computed(() => !!props.product.cart_item_id);

const modalClasses = computed(() => {
  return {
    'more-info-modal__in-basket': isInBasket.value,
    'more-info-modal__removed-from-cart': props.product.removedFromCart,
  }
});

const travelInsuranceButtonText = computed(() => {
  if (travelInsuranceStore.quoteStarted) {
    return proxy.$t('searchExtras.travelInsurance.continueQuote');
  }
  return proxy.$t('searchExtras.travelInsurance.button');
});

function closeModal() {
  emit('close');
}

function goToTab(tabId: string) {
  const targetElement = document.getElementById(tabId);

  if (!targetElement) {
    console.warn(`Element with id "${tabId}" not found`);
    return;
  }

  datadogRum.addAction('more-info-modal', { value: tabId });

  //sets the flag to prevent scroll listener from interfering
  isScrolling.value = true;

  localTabs.value.forEach((tab) => {
    tab.active = tab.id === tabId;
  });

  targetElement.scrollIntoView({
    behavior: 'smooth',
    block: 'start',
    inline: 'nearest'
  });

  //clears the flag after scrolling animation ends
  setTimeout(() => {
    isScrolling.value = false;
  }, 300);
}

function checkIfTabsScrollable() {
  const el = document.querySelector('.more-info-modal__nav-tabs');
  if (el instanceof HTMLElement) {
    isTabsScrollable.value = el.scrollWidth > el.clientWidth;
  }
}

function navTabsScrollHandler(ev: Event) {
  if (ev.target instanceof HTMLElement) {
    isTabsScrollAtTheEnd.value = ev.target.scrollLeft === ev.target.scrollWidth - ev.target.clientWidth;
  }
}

function scrollLeftIconClickHandler() {
  const el = document.querySelector('.more-info-modal__nav-tabs');
  if (el instanceof HTMLElement) {
    el.scrollTo({ left: -100, behavior: 'smooth' });
  }
}

function scrollRightIconClickHandler() {
  const el = document.querySelector('.more-info-modal__nav-tabs');
  if (el instanceof HTMLElement) {
    el.scrollTo({ left: 100, behavior: 'smooth' });
  }
}

function initIntersectionObserver() {
  const scrollArea = document.querySelector('.more-info-modal__scroll-area');
  if (!scrollArea) return;

  //scroll listener
  const handleScroll = () => {
    if (isScrolling.value) return;

    // Debounce scroll updates
    if (scrollTimeout.value) {
      clearTimeout(scrollTimeout.value);
    }

    scrollTimeout.value = setTimeout(() => {
      const scrollTop = scrollArea.scrollTop;

      //this is to find which section ismost visible based on scroll position
      let activeTabId = null;

      //get all sections that have tabs
      const sections = [];
      localTabs.value.forEach(tab => {
        const element = document.getElementById(tab.id);
        if (element) {
          sections.push({
            id: tab.id,
            element: element,
            top: element.offsetTop - scrollArea.offsetTop
          });
        }
      });

      //sorting sections by position
      sections.sort((a, b) => a.top - b.top);

      //finding section that is visible the most
      for (let i = sections.length - 1; i >= 0; i--) {
        if (scrollTop >= sections[i].top - 10) {
          activeTabId = sections[i].id;
          break;
        }
      }

      if (!activeTabId && sections.length > 0) {
        activeTabId = sections[0].id;
      }

      //to update active tab
      if (activeTabId) {
        localTabs.value.forEach(tab => {
          tab.active = tab.id === activeTabId;
        });
      }
    }, 100);
  };

  scrollArea.addEventListener('scroll', handleScroll, { passive: true });
  window.moreInfoScrollHandler = handleScroll;
}

function cleanupObserver() {
  const scrollArea = document.querySelector('.more-info-modal__scroll-area');
  if (scrollArea && window.moreInfoScrollHandler) {
    scrollArea.removeEventListener('scroll', window.moreInfoScrollHandler);
    delete window.moreInfoScrollHandler;
  }
  if (scrollTimeout.value) {
    clearTimeout(scrollTimeout.value);
  }
}

function getSanitizedHTML(html: string): string {
  return sanitizeHTML(html);
}

//watcher for tabs prop to update localTabs
watch(
  () => [props.tabs, isInBasket.value, props.product.removedFromCart],
  ([newTabs, inBasket, removedFromCart]) => {
    const baseTabs = newTabs ? newTabs.map(tab => ({ ...tab })) : [];

    //clean up old observer before changing tabs
    cleanupObserver();

    if (window.innerWidth < 1024 && (inBasket || removedFromCart)) {
      const quoteTab = {
        id: 'your-quote',
        translationKey: 'searchExtras.travelInsurance.yourQuote',
        visible: true,
        active: true,
        content: null
      };

      localTabs.value = [
        quoteTab,
        ...baseTabs.map(tab => ({ ...tab, active: false }))
      ];
    } else {
      localTabs.value = baseTabs.map((tab, index) => ({
        ...tab,
        active: index === 0
      }));
    }

    //reinitialize observer after tabs change
    nextTick(() => {
      initIntersectionObserver();
    });
  },
  { immediate: true }
);

expose({
  closeModal,
  goToTab,
  localTabs
});

onMounted(() => {
  const baseTabs = props.tabs.map(tab => ({ ...tab }));

  if (window.innerWidth < 1024 && (isInBasket.value || props.product.removedFromCart)) {
    const quoteTab = {
      id: 'your-quote',
      translationKey: 'searchExtras.travelInsurance.yourQuote',
      visible: true,
      active: true,
      content: null
    };

    localTabs.value = [
      quoteTab,
      ...baseTabs.map(tab => ({ ...tab, active: false }))
    ];
  } else {
    localTabs.value = baseTabs.map((tab, index) => ({
      ...tab,
      active: index === 0
    }));
  }

  nextTick(() => {
    checkIfTabsScrollable();
    initIntersectionObserver();
  });
});

onUnmounted(() => {
  cleanupObserver();
});

return { __sfc: true,IconArrow, IconClose, IconSuccess, IconPlus, props, emit, proxy, travelInsuranceStore, moreInfoModal, isTabsScrollable, isTabsScrollAtTheEnd, showProductLogo, localTabs, isScrolling, scrollTimeout, isInBasket, modalClasses, travelInsuranceButtonText, closeModal, goToTab, checkIfTabsScrollable, navTabsScrollHandler, scrollLeftIconClickHandler, scrollRightIconClickHandler, initIntersectionObserver, cleanupObserver, getSanitizedHTML, CavuButton, TravelInsuranceQuoteDetails, PoliciesMoreInfoModal }
}

})